import type { Audio } from '@/data';
import { Piece } from '@/data';
import type { TMeasureMap } from '@/data/MeasureMap';
import type { ImageLikeSource } from '@/data/sources';
import type { LyricsFileFormat } from '@/formats/inputSourcesParsers/LyricsParser';
import type { PieceSettings } from '@/data/Piece';
import type { Image3DFileFormat, ImageFileFormat } from '@/data/sources/Source';
import type { AudioFileFormat } from './inputSourcesParsers/AudioParser';
import type { SignatureFormat } from './SignatureParser';
import SignatureParser from './SignatureParser';
import MetadataParser from './MetadataParser';
import type { LocalizedTitle, LocalizedTitleKey } from '../data/Localized';

/**
  * All the displayable sources for the piece
  * May include scores, audio files and waves
  */
export interface SourcesFormat {
  images?: Array<ImageFileFormat | Image3DFileFormat>;
  audios?: AudioFileFormat[];
  lyrics?: LyricsFileFormat[];
}

/**
  * A parsed .dezrann file
  * Contains all relevant info to load and display the piece
  */
export interface PieceFormat extends LocalizedTitle {
  'id': string;
  'title': string;
  'composer'?: string;
  'editor'?: 'dez-edit-bar';
  'time-signature'?: SignatureFormat;
  'time-signature-upbeat'?: number;
  'last-offset'?: number;

  'opus': Record<string, unknown>;
  'sources': SourcesFormat | Record<string, unknown>[];
  'analysis:default'?: string;
  'quality:audio'?: number;
  'quality:audio:synchro'?: number;
  'quality:musical-time'?: number;

  'analysis:grid'?: string;

  settings?: PieceSettings;
  'permissions'?: Array<string>;
}

function checkString(val: unknown): string {
  if (typeof val === 'number') return val.toString();
  if (typeof val !== 'string') {
    throw new Error(`Corrupted Piece: ${val} is not a string`);
  }
  return val;
}

export default class PieceParser extends Piece {
  static fromFormat(
    path: string,
    isLocal: boolean,
    pieceFormat: PieceFormat,
    sources: ImageLikeSource[] = [],
    tracks: Audio[] = [],
  ): Piece {
    const id = checkString(pieceFormat.id);
    const opus = pieceFormat.opus || {};

    const title = checkString(pieceFormat.title || '');
    // const lastOffset = Number.parseFloat(format['last-offset']?.toString()) || ;

    const signature = SignatureParser.fromFormat(
      (pieceFormat.opus && pieceFormat.opus['measure-map']) ? pieceFormat.opus['measure-map'] as unknown as TMeasureMap : pieceFormat['time-signature'] || '' as SignatureFormat | TMeasureMap,
      pieceFormat['time-signature-upbeat'] || 0,
    );

    const composer = checkString(pieceFormat.composer || '');

    const localizedTitles: LocalizedTitle = {};
    Object.keys(pieceFormat).forEach((key) => {
      const [splittedPrefix, splittedSuffix] = key.split(':');
      if (splittedPrefix === 'title' && splittedSuffix) {
        const localeTitle = checkString((pieceFormat as unknown as Record<string, string>)[key]);
        localizedTitles[splittedSuffix as LocalizedTitleKey] = localeTitle;
      }
    });

    const refs: Record<string, string> = {};

    Object.keys(opus).forEach((k) => {
      if (k.substring(0, 3) === 'ref') {
        refs[k.substring(4)] = checkString((opus as unknown as Record<string, string>)[k]);
      }
    });

    const metadata = MetadataParser.fromFormat(opus);

    const refKeys = Object.keys(opus).filter((k) => k.startsWith('ref:'));

    Object.keys(metadata).forEach((k) => { delete opus[k]; });
    refKeys.forEach((k) => { delete opus[k]; });

    opus.metadata = metadata;

    const openWithCreationBar = pieceFormat.editor === 'dez-edit-bar';

    // See #910
    // eslint-disable-next-line max-len
    const availableScores = Array.isArray(pieceFormat.sources) ? 0 : pieceFormat.sources?.images?.length ?? 0;
    // eslint-disable-next-line max-len
    const availableTracks = Array.isArray(pieceFormat.sources) ? 0 : pieceFormat.sources?.audios?.length ?? 0;

    const availableSources = availableScores + availableTracks;
    const availableWaves = availableSources - availableScores; // Now = availableTracks

    const audioQuality = (pieceFormat['quality:audio:synchro'] ?? 3) < 3
      ? 0
      : pieceFormat['quality:audio'] ?? 0;
    const musicalQuality = pieceFormat['quality:musical-time'] ?? 3;

    const settings: PieceSettings = {
      'score-minimap': pieceFormat?.settings?.['score-minimap'] === undefined ? true : pieceFormat.settings?.['score-minimap'],
      panels: pieceFormat?.settings?.panels,
      'grid:row': pieceFormat?.settings?.['grid:row'],
      'grid:lines': pieceFormat?.settings?.['grid:lines'],
      'analysis:default': checkString(pieceFormat.settings?.['analysis:default'] ?? pieceFormat['analysis:default'] ?? ''),
      'analysis:grid': checkString(pieceFormat.settings?.['analysis:grid'] ?? pieceFormat['analysis:grid'] ?? ''),
    };
    const permissions = pieceFormat.permissions || null;

    const defaultAnalysis = settings['analysis:default'] || null;
    const curatedAnalyses = defaultAnalysis ? 1 : 0;

    const analysisGrid = settings['analysis:grid'] || null;

    return new Piece({
      id,
      path,
      isLocal,
      title,
      sources,
      tracks,
      availableSources,
      availableTracks,
      signature,
      openWithCreationBar,
      // lastOffset,
      composer,
      localizedTitles,
      availableScores,
      availableWaves,
      curatedAnalyses,
      opus,
      refs,
      defaultAnalysis,
      audioQuality,
      musicalQuality,
      settings,
      permissions,
      analysisGrid,
    });
  }
}
